import { EMPTY, Subject, timer } from 'rxjs';
import { filter, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { webSocket } from 'rxjs/webSocket';
export class WebSocketEventBus {
    constructor(options) {
        Object.defineProperty(this, "url", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // User credentials getter to be sent to server for authentication.
        Object.defineProperty(this, "getCredentials", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // wsSubject is responsible for handling websocket connection
        Object.defineProperty(this, "wsSubject$", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // connectionSubscription is responsible for passing messages to the messageSubject$.
        Object.defineProperty(this, "connectionSubscription", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // messageSubject is responsible for providing message subscriptions to UI components.
        Object.defineProperty(this, "messageSubject$", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Subject()
        });
        // connectionEstablished serves as a switch to allow reconnection attempts.
        Object.defineProperty(this, "connectionEstablished$", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        // shouldReconnect is a trigger for reconnection.
        Object.defineProperty(this, "shouldReconnect", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "reconnectionAttempts", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "reconnect$", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Subject()
        });
        Object.defineProperty(this, "disconnect$", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Subject()
        });
        Object.defineProperty(this, "connecting", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "reconnectDelayMs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        }); // delay between attempts
        Object.defineProperty(this, "maxReconnectAttempts", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.url = options.url;
        this.getCredentials = options.getCredentials;
        this.wsSubject$ = null;
        this.connectionSubscription = null;
        this.reconnectDelayMs = options.reconnectDelayMs ?? 30000;
        this.maxReconnectAttempts = options.maxReconnectAttempts ?? 100;
    }
    async getAuthMessage() {
        const { token, client } = await this.getCredentials();
        return {
            action: 'auth',
            authToken: token,
            serviceName: client,
        };
    }
    async initializeWebSocketConnection() {
        // prevent multiple sessions creation
        if (this.wsSubject$ || this.connecting)
            return;
        this.connecting = true;
        try {
            this.shouldReconnect = true;
            this.connectionEstablished$ = new Subject();
            const credentials = await this.getAuthMessage();
            // Setup loop trying to establish connection once every `reconnectDelayMs`.
            // As soon as connection is established, the loop will stop.
            // It initializes a websocket connection subject that will pass the messages to the messageSubject$,
            // responsible for handling the stream of messages.
            const connect$ = timer(0, this.reconnectDelayMs).pipe(takeUntil(this.connectionEstablished$), switchMap(() => {
                // This case handles potential edge case if this operator is called after the connection has been established.
                // In this case, we return an empty observable.
                if (!this.shouldReconnect || this.wsSubject$) {
                    return EMPTY;
                }
                if (this.maxReconnectAttempts &&
                    this.reconnectionAttempts >= this.maxReconnectAttempts) {
                    console.warn(`WebSocketEventBus: reached max reconnect attempts (${this.maxReconnectAttempts}).`);
                    this.shouldReconnect = false;
                    this.connectionEstablished$?.next(); // stop the timer loop
                    return EMPTY;
                }
                // Initialize websocket connection and immediately send authorization credentials
                // to confirm the connection using `wsSubject$.next()` method - it sends a message
                // via the websocket channel to the server.
                const socket = webSocket({
                    url: this.url,
                    openObserver: {
                        next: () => {
                            // stop the retry-timer on first open
                            this.connectionEstablished$?.next();
                            this.reconnect$.next();
                        },
                    },
                    closeObserver: {
                        next: () => {
                            this.disconnect$.next();
                        },
                    },
                });
                // assign after creation so we can close the socket it when it dies, not the one that replaced it.
                this.wsSubject$ = socket;
                this.reconnectionAttempts++;
                // Before returning the observable, we set up the logic for handling connection events,
                // such as connection establishing and reconnection in case of error or connection
                // closing from server side (in case of client timeout).
                // If server closes the connection, observable will trigger `complete` method,
                // and when observable completes, `finalize` operator is triggered.
                const wsObservable = socket.pipe(tap({
                    complete: () => {
                        // Only enable reconnection if it wasn't explicitly disabled (e.g. by disconnect())
                        // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
                        if (this.shouldReconnect !== false) {
                            this.shouldReconnect = true;
                        }
                    },
                }), finalize(() => {
                    // If the connection was completed or an error was received,
                    // we complete all subscriptions and set up a new connection attempt.
                    socket.complete(); // close the socket that has to be closed
                    this.connectionEstablished$?.complete();
                    this.connectionSubscription?.unsubscribe();
                    // reset fields only if they still point to this socket
                    if (this.wsSubject$ === socket)
                        this.wsSubject$ = null;
                    this.connectionEstablished$ = null;
                    this.connectionSubscription = null;
                    if (this.shouldReconnect) {
                        if (!this.maxReconnectAttempts ||
                            this.reconnectionAttempts < this.maxReconnectAttempts) {
                            setTimeout(() => void this.initializeWebSocketConnection(), this.reconnectDelayMs);
                        }
                        else {
                            console.warn(`WebSocketEventBus: reached max reconnect attempts (${this.maxReconnectAttempts}).`);
                        }
                    }
                }));
                // Send authentication credentials.
                socket.next(credentials);
                return wsObservable;
            }));
            // Just for clarity we split websocket connection handling and
            // actual business-logic between wsSubject and messageSubject.
            // wsSubject subscribes to the stream of ws-messages and passes them to messageSubject.
            // In turn, UI components will be able to subscribe to messageSubject stream and receive messages
            // where necessary.
            this.connectionSubscription = connect$.subscribe({
                next: (message) => {
                    // handling errors inside valid messages
                    if ('error' in message && message.error) {
                        console.error('WS connection failed, closing socket for retry:', message.error);
                        // unsubscribe to trigger the finalize() cleanup & retry logic
                        this.connectionSubscription?.unsubscribe();
                        return;
                    }
                    this.messageSubject$.next(message);
                },
                error: (err) => {
                    // Handle WebSocket errors by enabling reconnection
                    console.error('WebSocket Connection Error:', err);
                    this.shouldReconnect = true;
                },
            });
        }
        finally {
            this.connecting = false;
        }
    }
    connect() {
        void this.initializeWebSocketConnection();
    }
    // If we need to permanently close connection, we can call this method.
    disconnect() {
        // Prevent any reconnection attempts
        this.shouldReconnect = false;
        // Unsubscribing will close the connection and trigger `finalize` operator logic for cleanup.
        this.connectionSubscription?.unsubscribe();
        // close any active socket immediately
        this.wsSubject$?.complete();
    }
    // This method sends a message to the server via the websocket channel.
    emit(event) {
        if (!this.wsSubject$)
            return;
        this.wsSubject$.next(event);
    }
    // This is a helper method for filtering messages from Event Bus based on the operation type.
    onOperation(operation) {
        return this.messageSubject$.asObservable().pipe(filter((event) => event.operation === operation), map((event) => event.data));
    }
    // This is a helper method for filtering messages from Event Bus based on the operation type and entity name.
    onEntity({ name, operation, }) {
        return this.messageSubject$.asObservable().pipe(filter((event) => {
            if (!('operation' in event) || !('entityName' in event.data))
                return false;
            return event.data.entityName === name && event.operation === operation;
        }), map((event) => event.data));
    }
    // This is a helper method for filtering messages from Event Bus based on the operation type and relation name.
    onRelation({ name, operation, }) {
        return this.messageSubject$.asObservable().pipe(filter((event) => {
            if (!('operation' in event) || !('n2nRelationshipName' in event.data)) {
                return false;
            }
            return event.data.n2nRelationshipName === name && event.operation === operation;
        }), map((event) => event.data));
    }
    onReconnect() {
        return this.reconnect$.asObservable();
    }
    onDisconnect() {
        return this.disconnect$.asObservable();
    }
    // Service method in case if we need to setup a direct observable subscription for messageSubject.
    subscribe(observableOrNext) {
        if (!observableOrNext)
            return;
        return this.messageSubject$.subscribe(observableOrNext);
    }
    asObservable() {
        return this.messageSubject$.asObservable();
    }
}
